import React, {useEffect, useState} from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import Layout from '../components/Layout';
import {api} from '../../api';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const STAGE_ORDER = ['DISCOVERY','QUALIFIED','PROPOSAL','NEGOTIATION','CLOSED_WON','CLOSED_LOST'];
const STAGE_COLORS = {DISCOVERY:'#9333ea',QUALIFIED:'#2563eb',PROPOSAL:'#eab308',NEGOTIATION:'#f97316',CLOSED_WON:'#16a34a',CLOSED_LOST:'#dc2626'};
const ACTIVITY_COLORS = {CALL:'#2563eb',EMAIL:'#9333ea',MEETING:'#f97316',LINKEDIN_MESSAGE:'#0ea5e9'};

function Card({title, children}) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Stat({label, value, sub}) {
  return (
    <div className="bg-white rounded-lg shadow p-6 text-center">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-sm text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function Reports() {
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [userMap, setUserMap] = useState({});

  useEffect(() => {
    api('/deals').then(r => r.json()).then(setDeals).catch(() => {});
    api('/contacts').then(r => r.json()).then(setContacts).catch(() => {});
    api('/crm-activities').then(r => r.json()).then(setActivities).catch(() => {});
    api('/tenant-users').then(r => r.json()).then(users => {
      const m = {};
      (users || []).forEach(u => { m[u.userId] = `${u.firstName} ${u.lastName}`.trim() || u.username; });
      setUserMap(m);
    }).catch(() => {});
  }, []);

  // Revenue by month (closed won)
  const revenueByMonth = (() => {
    const byMonth = {};
    deals.filter(d => d.stage === 'CLOSED_WON' && d.closeDate).forEach(d => {
      const date = new Date(d.closeDate);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      byMonth[key] = (byMonth[key] || 0) + (d.value || 0);
    });
    return Object.keys(byMonth).sort().slice(-12).map(key => ({
      month: MONTHS[parseInt(key.split('-')[1], 10) - 1] + ' ' + key.split('-')[0].slice(2),
      revenue: byMonth[key],
    }));
  })();

  // Pipeline funnel
  const pipelineData = STAGE_ORDER.filter(s => !['CLOSED_WON','CLOSED_LOST'].includes(s)).map(stage => {
    const stageDeals = deals.filter(d => d.stage === stage);
    return {name: stage.replace(/_/g, ' '), count: stageDeals.length, value: stageDeals.reduce((s, d) => s + (d.value || 0), 0), fill: STAGE_COLORS[stage]};
  });

  // Win/loss
  const won = deals.filter(d => d.stage === 'CLOSED_WON').length;
  const lost = deals.filter(d => d.stage === 'CLOSED_LOST').length;
  const winRate = won + lost > 0 ? Math.round(won / (won + lost) * 100) : 0;
  const winLossData = [{name: 'Won', value: won, fill: '#16a34a'}, {name: 'Lost', value: lost, fill: '#dc2626'}].filter(d => d.value > 0);

  // Deal velocity (avg days to close)
  const closedDeals = deals.filter(d => ['CLOSED_WON','CLOSED_LOST'].includes(d.stage) && d.closeDate && d.createdAt);
  const avgDays = closedDeals.length > 0
    ? Math.round(closedDeals.reduce((sum, d) => sum + (new Date(d.closeDate) - new Date(d.createdAt)) / 86400000, 0) / closedDeals.length)
    : 0;

  // Totals
  const totalRevenue = deals.filter(d => d.stage === 'CLOSED_WON').reduce((s, d) => s + (d.value || 0), 0);
  const pipelineValue = deals.filter(d => !['CLOSED_WON','CLOSED_LOST'].includes(d.stage)).reduce((s, d) => s + (d.value || 0), 0);
  const avgDealSize = won > 0 ? Math.round(totalRevenue / won) : 0;

  // Contacts added by month
  const contactsByMonth = (() => {
    const byMonth = {};
    contacts.forEach(c => {
      if (!c.createdAt) return;
      const date = new Date(c.createdAt);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      byMonth[key] = (byMonth[key] || 0) + 1;
    });
    return Object.keys(byMonth).sort().slice(-12).map(key => ({
      month: MONTHS[parseInt(key.split('-')[1], 10) - 1] + ' ' + key.split('-')[0].slice(2),
      count: byMonth[key],
    }));
  })();

  // Activity by type
  const activityByType = (() => {
    const byType = {};
    activities.forEach(a => { byType[a.type] = (byType[a.type] || 0) + 1; });
    return Object.entries(byType).map(([type, count]) => ({name: type.replace(/_/g, ' '), value: count, fill: ACTIVITY_COLORS[type] || '#6b7280'}));
  })();

  // Activity by user (top 10)
  const activityByUser = (() => {
    const byUser = {};
    activities.forEach(a => { if (a.performedBy) byUser[a.performedBy] = (byUser[a.performedBy] || 0) + 1; });
    return Object.entries(byUser)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([userId, count]) => ({name: userMap[userId] || userId.slice(0, 8), count}));
  })();

  return (
    <Layout>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Reports</h1>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Stat label="Total Revenue" value={`$${totalRevenue.toLocaleString()}`} />
        <Stat label="Pipeline Value" value={`$${pipelineValue.toLocaleString()}`} />
        <Stat label="Win Rate" value={`${winRate}%`} sub={`${won}W / ${lost}L`} />
        <Stat label="Avg Deal Size" value={`$${avgDealSize.toLocaleString()}`} />
        <Stat label="Avg Days to Close" value={String(avgDays)} />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card title="Revenue by Month">
          {revenueByMonth.length === 0 ? <p className="text-gray-400 text-center py-8">No closed deals yet</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={revenueByMonth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{fontSize: 12}} />
                <YAxis tick={{fontSize: 12}} />
                <Tooltip formatter={v => `$${v.toLocaleString()}`} />
                <Bar dataKey="revenue" fill="#2563eb" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Deal Pipeline">
          {pipelineData.every(d => d.count === 0) ? <p className="text-gray-400 text-center py-8">No active deals</p> : (
            <div>
              {pipelineData.map(stage => {
                const maxCount = Math.max(...pipelineData.map(s => s.count), 1);
                return (
                  <div key={stage.name} className="flex items-center gap-3 mb-3">
                    <span className="text-xs text-gray-600 w-24 text-right">{stage.name}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-7 relative">
                      <div className="h-7 rounded-full flex items-center px-3" style={{width: `${Math.max(stage.count / maxCount * 100, 8)}%`, backgroundColor: stage.fill}}>
                        <span className="text-xs text-white font-medium">{stage.count}</span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 w-20">${stage.value.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card title="Win / Loss">
          {winLossData.length === 0 ? <p className="text-gray-400 text-center py-8">No closed deals</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={winLossData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} label={({name, value}) => `${name}: ${value}`}>
                  {winLossData.map(d => <Cell key={d.name} fill={d.fill} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Activity Breakdown">
          {activityByType.length === 0 ? <p className="text-gray-400 text-center py-8">No activities</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={activityByType} dataKey="value" cx="50%" cy="50%" outerRadius={80} label={({name, value}) => `${name}: ${value}`}>
                  {activityByType.map(d => <Cell key={d.name} fill={d.fill} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Top Performers">
          {activityByUser.length === 0 ? <p className="text-gray-400 text-center py-8">No activities</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={activityByUser} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} tick={{fontSize: 12}} />
                <YAxis type="category" dataKey="name" width={80} tick={{fontSize: 11}} />
                <Tooltip />
                <Bar dataKey="count" fill="#2563eb" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Charts row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Contacts Added by Month">
          {contactsByMonth.length === 0 ? <p className="text-gray-400 text-center py-8">No contacts yet</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={contactsByMonth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{fontSize: 12}} />
                <YAxis allowDecimals={false} tick={{fontSize: 12}} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#9333ea" strokeWidth={2} dot={{r: 4}} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Deals Created by Month">
          {(() => {
            const byMonth = {};
            deals.forEach(d => {
              if (!d.createdAt) return;
              const date = new Date(d.createdAt);
              const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
              byMonth[key] = (byMonth[key] || 0) + 1;
            });
            const data = Object.keys(byMonth).sort().slice(-12).map(key => ({
              month: MONTHS[parseInt(key.split('-')[1], 10) - 1] + ' ' + key.split('-')[0].slice(2),
              count: byMonth[key],
            }));
            return data.length === 0 ? <p className="text-gray-400 text-center py-8">No deals yet</p> : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{fontSize: 12}} />
                  <YAxis allowDecimals={false} tick={{fontSize: 12}} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={2} dot={{r: 4}} />
                </LineChart>
              </ResponsiveContainer>
            );
          })()}
        </Card>
      </div>
    </Layout>
  );
}
